/**
 * This is a dictionary of the different types of steps a Dasher could
 * take in the context of a delivery issue. The component receives a
 * unique step type as a key and matches the key to the appropriate
 * content using a template.
 */

import * as React from 'react'

import {
  DeliveryIssueStepResults,
  DeliveryIssueStepTypes,
} from '../../../../models/types'
import DeliveryIssueStepTemplate from './DeliveryIssueStepTemplate'
import t from './strings'

const {
  DxEnteredGeoFence,
  DxContactedCx,
  DxWaited,
  DxLeftPhoto,
} = DeliveryIssueStepTypes

/**
 * To designate the position in a list of delivery issue steps so the first
 * and the last steps can be targeted for special rendering cases.
 */
export enum StepPositions {
  First = 'first',
  Last = 'last',
  Other = 'other',
}

type DeliveryIssueStepDictionaryParams = {
  result: DeliveryIssueStepResults
  position: StepPositions
}

// A dictionary of all the types of steps that could pertain to an issue
export const DeliveryIssueStepDictionary = ({
  result,
  position,
}: DeliveryIssueStepDictionaryParams): React.ReactNode => ({
  [DxEnteredGeoFence]: (
    <DeliveryIssueStepTemplate
      title={t.dxEnteredGeoFenceTitleText()}
      subtext={t.dxEnteredGeoFenceDescriptionText()}
      result={result}
      position={position}
    />
  ),
  [DxContactedCx]: (
    <DeliveryIssueStepTemplate
      title={t.dxContactedCxTitleText()}
      subtext={t.dxContactedCxDescriptionText()}
      result={result}
      position={position}
    />
  ),
  [DxWaited]: (
    <DeliveryIssueStepTemplate
      title={t.dxWaitedTitleText()}
      subtext={t.dxWaitedDescriptionText()}
      result={result}
      position={position}
    />
  ),
  [DxLeftPhoto]: (
    <DeliveryIssueStepTemplate
      title={t.dxLeftPhotoTitleText()}
      subtext={t.dxLeftPhotoDescriptionText()}
      result={result}
      position={position}
    />
  ),
})

type DeliveryIssueStepProps = {
  type: DeliveryIssueStepTypes
  result: DeliveryIssueStepResults
  position: StepPositions
}

// The generic component used to render a specific issue step
const DeliveryIssueStep: React.FC<DeliveryIssueStepProps> = ({
  type,
  result,
  position,
}) => {
  return DeliveryIssueStepDictionary({ result, position })[type]
}

export default DeliveryIssueStep
